import { useEffect, useMemo, useState, type FormEvent, type JSX, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SPECIES_META,
  WORLD_THEMES,
  buildRoomPath,
  type DuckColor,
  type DuckSpecies,
  type RoomSummaryDto,
  type SessionSummaryDto,
} from '@duck/contracts';
import { api, type ApiError } from '../../lib/api';
import { usePageMeta } from '../../lib/page-meta';
import { useAuthStore } from '../../state/auth.store';
import { ErrorText, Field, SubmitButton } from '../../components/ui';
import { LivingBackdrop } from '../onboarding/LivingBackdrop';
import { SpeciesPicker, CritterPreview } from '../onboarding/CharacterPicker';
import { DuckPicker } from '../onboarding/DuckPicker';
import { ROOM_TYPES, type RoomLevelId } from '../rooms/room-types';

/** `#rrggbb` from a contracts palette int, so a card can wear its world's accent colour. */
function hex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

/** Title-case a colour id for the collapsed read-out ("blue" → "Blue"). */
function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * "Your character" (ADR-0024) — a persistent, collapsible panel in the lobby (the hub) to set your
 * ANIMAL + tint before you walk into any room. Your pick lives on your ACCOUNT (not per-room), so it's
 * carried into every room you enter; the change is applied on the next entry (appearance is sent at
 * realtime connect, so an already-open room isn't disturbed). Collapsed by default so it never competes
 * with the room shelves; the live WYSIWYG {@link CritterPreview} (a Pixi stage) mounts only when open.
 * Reuses the same pickers as registration + invites, so the choice feels identical everywhere.
 */
function YourCharacterCard(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const lastIdentity = useAuthStore((s) => s.lastIdentity);
  const setAppearance = useAuthStore((s) => s.setAppearance);
  const setAuth = useAuthStore((s) => s.setAuth);

  // The SAVED identity drives the collapsed header; the fields below edit a local DRAFT until you Save.
  const savedName = user?.displayName ?? lastIdentity?.displayName ?? 'Duck';
  const savedSpecies: DuckSpecies = user?.species ?? lastIdentity?.species ?? 'duck';
  const savedColor: DuckColor = user?.duckColor ?? lastIdentity?.duckColor ?? 'yellow';

  const [open, setOpen] = useState(false);
  const [name, setName] = useState(savedName);
  const [species, setSpecies] = useState<DuckSpecies>(savedSpecies);
  const [duckColor, setDuckColor] = useState<DuckColor>(savedColor);

  // Picking an animal snaps the tint to its natural default (still recolourable). Mirrors register/join.
  const onSpeciesChange = (next: DuckSpecies): void => {
    setSpecies(next);
    setDuckColor(SPECIES_META[next].defaultColor);
  };

  // One Save applies whatever changed. A RENAME rotates the token (the name is in the JWT — the room
  // reads it on join), so it returns a fresh session we fold in via setAuth; a LOOK change doesn't touch
  // the token (setAppearance). Rename FIRST so the fresh token is already in place before the look call.
  const mutation = useMutation({
    mutationFn: async (vars: { name: string; species: DuckSpecies; duckColor: DuckColor }) => {
      if (vars.name !== savedName) {
        setAuth(await api.updateDisplayName({ displayName: vars.name }));
      }
      if (vars.species !== savedSpecies || vars.duckColor !== savedColor) {
        const u = await api.updateAppearance({ species: vars.species, duckColor: vars.duckColor });
        setAppearance({ duckColor: u.duckColor, species: u.species });
      }
    },
  });

  const trimmedName = name.trim();
  const nameValid = trimmedName.length >= 2 && trimmedName.length <= 32;
  const dirty = trimmedName !== savedName || species !== savedSpecies || duckColor !== savedColor;

  // Sync the draft to the saved identity each time the panel opens (so it never shows a stale edit).
  const toggle = (): void => {
    setOpen((wasOpen) => {
      if (!wasOpen) {
        setName(savedName);
        setSpecies(savedSpecies);
        setDuckColor(savedColor);
        mutation.reset();
      }
      return !wasOpen;
    });
  };

  const onSave = (e: FormEvent): void => {
    e.preventDefault();
    if (!dirty || !nameValid) return;
    mutation.mutate({ name: trimmedName, species, duckColor });
  };

  return (
    <section className="mb-6 overflow-hidden rounded-2xl border border-white/10 bg-black/25">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-white/5"
      >
        <span aria-hidden className="text-2xl leading-none">
          {SPECIES_META[savedSpecies].emoji}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold uppercase tracking-wider text-white/40">
            Your character
          </span>
          <span className="block truncate text-sm font-medium text-white/90">{savedName}</span>
          <span className="block truncate text-xs text-white/45">
            {SPECIES_META[savedSpecies].label} · {titleCase(savedColor)}
          </span>
        </span>
        <span aria-hidden className="shrink-0 text-sm font-medium text-amber-200/90">
          {open ? 'Close ▴' : 'Edit ▾'}
        </span>
      </button>

      {open && (
        <form onSubmit={onSave} className="border-t border-white/10 px-4 pb-4 pt-3">
          <ErrorText>{mutation.error ? (mutation.error as ApiError).message : null}</ErrorText>
          {/* Your PUBLIC name — the one over your duck + in chat/roster. Editing it re-issues your session
              (the room reads it from the token on join), so the new name shows the next time you walk in. */}
          <Field
            label="Display name"
            value={name}
            maxLength={32}
            placeholder="The name over your duck"
            onChange={(e) => setName(e.target.value)}
          />
          {trimmedName.length > 0 && !nameValid && (
            <p className="-mt-2 mb-3 text-xs text-amber-200/80">Name must be 2–32 characters.</p>
          )}
          <CritterPreview species={species} color={duckColor} />
          <SpeciesPicker value={species} onChange={onSpeciesChange} />
          <DuckPicker value={duckColor} onChange={setDuckColor} />
          <SubmitButton
            loading={mutation.isPending}
            disabled={mutation.isPending || !dirty || !nameValid}
          >
            {!dirty && mutation.isSuccess ? 'Saved ✓' : 'Save character'}
          </SubmitButton>
        </form>
      )}
    </section>
  );
}

/**
 * The LOBBY — the HUB (ADR-0055): the main way into any room. Behind the login wall (ТЗ §1): you browse
 * and enter rooms as YOUR account, and each room keeps your progress separately (ТЗ §7). Three shelves:
 * an always-present "Create a room" (unlimited, account-owned), "Your rooms" (every room you own, even
 * empty ones — so a room you made is always re-enterable), and "Rooms right now" (the live directory of
 * where the ducks are). A direct room link to a LOCKED room lands here with its password prompt open.
 * Occupancy is a warm "who's around to play with" signal, never a surveillance metric.
 */
export function LobbyPage(): JSX.Element {
  usePageMeta({ title: 'Lobby — Duck Duck Space', noindex: true });
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const tokens = useAuthStore((s) => s.tokens);
  const logout = useAuthStore((s) => s.logout);

  // A direct link to a LOCKED room (RoomGate) or a stale one arrives via router state (once, on mount).
  const nav = (location.state ?? {}) as { joinRoom?: RoomSummaryDto; roomGone?: string };
  const [joining, setJoining] = useState<RoomSummaryDto | null>(nav.joinRoom ?? null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<RoomSummaryDto | null>(null);
  const [gone, setGone] = useState<boolean>(Boolean(nav.roomGone));

  // Explicit sign-out: honour the intent FIRST — wipe the local session (lastIdentity too, so the next
  // screen reads unmistakably signed-out) and land on the sign-in wall, ready to sign back in. Revoking
  // this device's refresh token server-side is best-effort AFTER, so a slow/failing round-trip never
  // strands the person on the lobby. (Clearing the session also trips this page's RequireAuth to /login,
  // so we navigate there explicitly to match — no landing-page flash in between.)
  const handleLogout = (): void => {
    const refreshToken = tokens?.refreshToken;
    logout();
    navigate('/login');
    if (refreshToken) void api.logout(refreshToken).catch(() => undefined);
  };

  // Clear the one-shot router state so a refresh doesn't re-open the dialog / re-show the notice.
  useEffect(() => {
    if (nav.joinRoom || nav.roomGone) navigate('.', { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sessions = useQuery({
    queryKey: ['lobby-sessions'],
    queryFn: api.listSessions,
    // Live-ish: a lobby that doesn't breathe feels dead. 5s is plenty for "who's around".
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });
  const myRooms = useQuery({ queryKey: ['my-rooms'], queryFn: api.myRooms });

  const rooms = sessions.data ?? [];
  const owned = myRooms.data ?? [];
  // Live headcount by room, so the "Your rooms" shelf can show occupancy without an O(owned × sessions)
  // scan per render/poll tick. A room missing from the live feed (no ducks) reads 0.
  const occupancyByRoom = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of sessions.data ?? []) m.set(r.workspaceId, r.occupancy);
    return m;
  }, [sessions.data]);

  const enterRoom = (room: RoomSummaryDto | SessionSummaryDto): void => {
    navigate(buildRoomPath(room.theme, room.slug));
  };

  return (
    <div className="relative flex h-full w-full items-start justify-center overflow-y-auto p-4 sm:p-6">
      <LivingBackdrop />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="relative z-10 my-auto flex w-full max-w-2xl flex-col rounded-3xl border border-white/10 bg-slate-950/60 p-6 shadow-2xl backdrop-blur-xl sm:p-8"
      >
        <header className="mb-6 text-center">
          <div className="mb-2 text-4xl sm:text-5xl">🦆</div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Rooms</h1>
          <p className="mt-1 text-sm text-white/60">Create your own, or hop into one that&apos;s buzzing.</p>
        </header>

        {gone && (
          <div
            role="status"
            className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-amber-300/30 bg-amber-300/10 px-4 py-3 text-sm text-amber-100"
          >
            <span>That room isn&apos;t available anymore — pick another below.</span>
            <button
              type="button"
              onClick={() => setGone(false)}
              className="shrink-0 rounded-lg px-2 py-1 text-amber-200/80 transition hover:bg-white/10 hover:text-white"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}

        {/* Who you are, before you pick where you go (ADR-0024): a persistent, always-here place to set
            your animal + tint. Saved to your account, so every room you enter shows this critter. */}
        <YourCharacterCard />

        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mb-3 flex w-full items-center justify-center gap-2 rounded-2xl bg-amber-400 px-5 py-3 font-semibold text-slate-900 shadow-lg transition hover:bg-amber-300"
        >
          <span aria-hidden>✨</span> Create a room
        </button>

        {/* Map editor — the level-authoring creator tool, moved here from the public landing so it lives
            behind the login wall with the rest of the hub. The `?editor=1` flag opens + persists it (the
            editor is dev/admin-gated by default). A calm secondary button so it never competes with the
            primary "Create a room". */}
        <Link
          to="/editor?editor=1"
          data-testid="lobby-map-editor"
          className="mb-6 flex w-full items-center justify-center gap-2 rounded-2xl border border-white/15 px-5 py-3 font-semibold text-white/85 outline-none transition hover:border-white/35 hover:bg-white/5 focus-visible:ring-2 focus-visible:ring-amber-300/70"
        >
          <span aria-hidden>🗺️</span> Map editor
        </Link>

        {owned.length > 0 && (
          <section className="mb-6">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/40">Your rooms</h2>
            <ul className="flex flex-col gap-2">
              {owned.map((room) => (
                <RoomCard
                  key={room.workspaceId}
                  room={room}
                  occupancy={occupancyByRoom.get(room.workspaceId) ?? 0}
                  cta="Open"
                  onEnter={() => enterRoom(room)}
                  // Your rooms are deletable — except your HOME room (the account's anchor: deleting it
                  // would take your account with it), where the server refuses and the button is hidden.
                  onDelete={room.isHome ? undefined : () => setDeleting(room)}
                />
              ))}
            </ul>
          </section>
        )}

        <section>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-white/40">Rooms right now</h2>
          {sessions.isLoading ? (
            <p className="py-10 text-center text-white/50">Looking for open rooms…</p>
          ) : rooms.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-black/20 px-6 py-10 text-center">
              <div className="mb-3 text-4xl">🌱</div>
              <p className="text-white/70">No rooms are buzzing yet.</p>
              <p className="mt-1 text-sm text-white/50">Be the first — create a room and the lights come on.</p>
            </div>
          ) : (
            <ul className="flex flex-col gap-3">
              {rooms.map((room) => (
                <RoomCard
                  key={room.workspaceId}
                  room={room}
                  occupancy={room.occupancy}
                  cta="Join"
                  // A locked room opens the password prompt; an open one walks straight in via RoomGate.
                  onEnter={() => (room.hasPassword ? setJoining(room) : enterRoom(room))}
                />
              ))}
            </ul>
          )}
        </section>

        {/* Sign out of this device — a quiet footer control so it never competes with entering a room. */}
        <div className="mt-6 flex justify-center border-t border-white/10 pt-4">
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm text-white/50 outline-none transition hover:bg-white/5 hover:text-white/90 focus-visible:ring-2 focus-visible:ring-amber-300/70"
          >
            <span aria-hidden>🚪</span> Log out
          </button>
        </div>
      </motion.div>

      {creating && (
        <CreateRoomDialog
          onClose={() => setCreating(false)}
          onCreated={(theme, workspaceId) => navigate(buildRoomPath(theme, workspaceId))}
        />
      )}

      {joining && (
        <JoinDialog
          room={joining}
          onClose={() => setJoining(null)}
          onJoined={() => enterRoom(joining)}
        />
      )}

      {deleting && (
        <DeleteRoomDialog
          room={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            // The room is gone — refresh "Your rooms" (and the live feed, in case it was buzzing).
            void queryClient.invalidateQueries({ queryKey: ['my-rooms'] });
            void queryClient.invalidateQueries({ queryKey: ['lobby-sessions'] });
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}

/** One room row: theme dot, name, live headcount, a 🔒 if locked, and an enter button. Works for both an
 *  owned room (RoomSummaryDto) and a live session (SessionSummaryDto) — occupancy is passed in separately. */
function RoomCard({
  room,
  occupancy,
  cta,
  onEnter,
  onDelete,
}: {
  room: RoomSummaryDto | SessionSummaryDto;
  occupancy: number;
  cta: string;
  onEnter: () => void;
  /** When set, a trash button appears (only for a deletable room you own — never your home room). */
  onDelete?: () => void;
}): JSX.Element {
  const theme = WORLD_THEMES[room.theme];
  return (
    <li className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/25 px-4 py-3 transition hover:border-white/20 hover:bg-black/35">
      <span
        aria-hidden
        className="h-9 w-9 shrink-0 rounded-full ring-2 ring-white/10"
        style={{ background: hex(theme.palette.accent) }}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate font-semibold text-white">{room.name}</span>
          {room.hasPassword && (
            <span title="Password required" aria-label="Password required">
              🔒
            </span>
          )}
        </div>
        <div className="text-xs text-white/50">
          {theme.name} ·{' '}
          <span className="text-emerald-300/90">
            {occupancy} {occupancy === 1 ? 'duck' : 'ducks'} here
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={onEnter}
        className="shrink-0 rounded-xl bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
      >
        {cta}
      </button>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${room.name}`}
          title="Delete room"
          className="shrink-0 rounded-xl border border-white/10 p-2 text-white/40 outline-none transition hover:border-red-400/40 hover:bg-red-500/10 hover:text-red-300 focus-visible:ring-2 focus-visible:ring-red-400/60"
        >
          <span aria-hidden>🗑️</span>
        </button>
      )}
    </li>
  );
}

/**
 * Confirm before deleting a room you own — the destructive action gets a deliberate second tap (never a
 * one-click accident). Deleting is server-authoritative + irreversible: the room and all its furniture,
 * coins & progress are torn down. On success the caller refreshes the shelves. Esc / backdrop cancels.
 */
function DeleteRoomDialog({
  room,
  onClose,
  onDeleted,
}: {
  room: RoomSummaryDto;
  onClose: () => void;
  onDeleted: () => void;
}): JSX.Element {
  const mutation = useMutation({
    mutationFn: () => api.deleteRoom(room.workspaceId),
    onSuccess: onDeleted,
  });

  return (
    <DialogShell onClose={onClose} label={`Delete ${room.name}`}>
      <h2 className="mb-1 text-lg font-bold text-white">Delete this room?</h2>
      <p className="mb-4 text-sm text-white/60">
        <span className="font-semibold text-white/90">{room.name}</span> and everything in it — its
        furniture, coins &amp; progress — will be gone for good. This can&apos;t be undone.
      </p>
      <ErrorText>{mutation.error ? (mutation.error as ApiError).message : null}</ErrorText>
      <div className="mt-2 flex gap-3">
        <button
          type="button"
          onClick={onClose}
          className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 font-medium text-white/80 transition hover:bg-white/10"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="flex-1 rounded-xl bg-red-500 px-4 py-2.5 font-semibold text-white transition hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? 'Deleting…' : 'Delete room'}
        </button>
      </div>
    </DialogShell>
  );
}

/**
 * Create a brand-new room OWNED BY my account (ADR-0055 — unlimited). Pick a level, name it, optionally
 * lock it with a casual door-code. On success the server returns a token already scoped to the new room,
 * so we setAuth + walk straight in (via its deep-link, which RoomGate + the canonicalizer settle).
 */
function CreateRoomDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (theme: RoomLevelId, workspaceId: string) => void;
}): JSX.Element {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [theme, setTheme] = useState<RoomLevelId>('medieval_castle');
  const [locked, setLocked] = useState(false);
  const [password, setPassword] = useState('');

  const mutation = useMutation({
    mutationFn: api.createWorkspace,
    onSuccess: (result) => {
      setAuth(result);
      // The new room now exists + is owned by me — refresh "Your rooms" for when we come back.
      void queryClient.invalidateQueries({ queryKey: ['my-rooms'] });
      onCreated(theme, result.user.workspaceId);
    },
  });

  // Esc-to-close is owned by the surrounding DialogShell — no separate listener here.
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      name: name.trim(),
      theme,
      ...(locked && password ? { password } : {}),
    });
  };

  return (
    <DialogShell onClose={onClose} label="Create a room">
      <h2 className="mb-1 text-lg font-bold text-white">Create a room</h2>
      <p className="mb-4 text-sm text-white/55">
        Your own space, owned by you. Invite friends by sharing its link — lock it if you want a door code.
      </p>
      <form onSubmit={onSubmit}>
        <ErrorText>{mutation.error ? (mutation.error as ApiError).message : null}</ErrorText>

        <div className="mb-4">
          <span className="mb-1.5 block text-sm font-medium text-white/80">Level</span>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Room level">
            {ROOM_TYPES.map((rt) => {
              const selected = theme === rt.id;
              return (
                <button
                  key={rt.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setTheme(rt.id)}
                  className={`flex flex-col items-center gap-1 rounded-xl border p-3 text-center transition ${
                    selected
                      ? 'border-amber-300 bg-amber-300/10 ring-1 ring-amber-300/40'
                      : 'border-white/10 bg-white/5 hover:border-white/25'
                  }`}
                >
                  <span className="text-xl" aria-hidden>
                    {rt.icon}
                  </span>
                  <span className="text-xs font-semibold text-white">{rt.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Field
          label="Room name"
          required
          autoFocus
          placeholder="e.g. Team Nebula"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <label className="mb-2 mt-1 flex cursor-pointer items-center gap-2 text-sm text-white/75">
          <input type="checkbox" checked={locked} onChange={(e) => setLocked(e.target.checked)} />
          Lock with a password
        </label>
        {locked && (
          <Field
            label="Room password"
            type="password"
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}

        <div className="mt-2 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 font-medium text-white/80 transition hover:bg-white/10"
          >
            Cancel
          </button>
          <div className="flex-1">
            <SubmitButton loading={mutation.isPending}>Create &amp; enter</SubmitButton>
          </div>
        </div>
      </form>
    </DialogShell>
  );
}

/**
 * Drop-in dialog: enter a room as YOUR account (your name + duck come with you — no form needed). For a
 * 🔒 room, confirm the password. Submitting re-issues a token for this account scoped to the room and
 * loads that room's own progress; a wrong password surfaces inline. Esc / backdrop click cancels.
 */
function JoinDialog({
  room,
  onClose,
  onJoined,
}: {
  room: RoomSummaryDto;
  onClose: () => void;
  onJoined: () => void;
}): JSX.Element {
  const setAuth = useAuthStore((s) => s.setAuth);
  const [password, setPassword] = useState('');

  const mutation = useMutation({
    mutationFn: api.joinSession,
    onSuccess: (result) => {
      setAuth(result);
      onJoined();
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      workspaceId: room.workspaceId,
      ...(room.hasPassword ? { password } : {}),
    });
  };

  return (
    <DialogShell onClose={onClose} label={`Join ${room.name}`}>
      <h2 className="mb-1 text-lg font-bold text-white">
        {room.hasPassword ? '🔒 ' : ''}
        Join {room.name}
      </h2>
      <p className="mb-4 text-sm text-white/55">
        You&apos;ll enter as yourself — this room keeps its own coins &amp; items, separate from your other
        rooms.
        {room.hasPassword && " It's locked — enter its password to come in."}
      </p>
      <form onSubmit={onSubmit}>
        <ErrorText>{mutation.error ? (mutation.error as ApiError).message : null}</ErrorText>
        {room.hasPassword && (
          <Field
            label="Room password"
            type="password"
            required
            autoFocus
            autoComplete="off"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        )}
        <div className="mt-2 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 font-medium text-white/80 transition hover:bg-white/10"
          >
            Cancel
          </button>
          <div className="flex-1">
            <SubmitButton loading={mutation.isPending}>Walk in</SubmitButton>
          </div>
        </div>
      </form>
    </DialogShell>
  );
}

/** Shared modal chrome: dimmed backdrop, Esc/backdrop-click to close, centered card. */
function DialogShell({
  onClose,
  label,
  children,
}: {
  onClose: () => void;
  label: string;
  children: ReactNode;
}): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="w-full max-w-sm rounded-3xl border border-white/10 bg-slate-950/90 p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </motion.div>
    </div>
  );
}
